import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, RefreshCw, Activity, ArrowDownToLine, ArrowUpFromLine, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const timeAgo = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return new Date(iso).toLocaleString();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const StatusBadge = ({ ok, labelOk, labelBad }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
      ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
    }`}
  >
    {ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
    {ok ? labelOk : labelBad}
  </span>
);

const AdminSyncHealth = () => {
  const [authed, setAuthed] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [error, setError] = useState('');
  const [health, setHealth] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAlerts = useCallback(async (key) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/drive-alerts`, {
        headers: { 'X-Admin-Key': key },
      });
      if (res.ok) setAlerts((await res.json()).alerts || []);
    } catch { /* alerts are supplementary — never block the page */ }
  }, []);

  const dismissAlert = async (id) => {
    await fetch(`${BACKEND_URL}/api/admin/drive-alerts/${id}/resolve`, {
      method: 'POST',
      headers: { 'X-Admin-Key': adminKey },
    });
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  };

  const dismissAll = async () => {
    await fetch(`${BACKEND_URL}/api/admin/drive-alerts/resolve-all`, {
      method: 'POST',
      headers: { 'X-Admin-Key': adminKey },
    });
    setAlerts([]);
  };

  const fetchHealth = useCallback(async (key) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/sync-health`, {
        headers: { 'X-Admin-Key': key },
      });
      if (res.ok) {
        setHealth(await res.json());
        setError('');
        fetchAlerts(key);
        return true;
      }
      setError('Incorrect admin key');
      return false;
    } catch {
      setError('Unable to reach server. Try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchAlerts]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const ok = await fetchHealth(adminKey);
    if (ok) setAuthed(true);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-light px-4">
        <form onSubmit={handleLogin} className="bg-white p-8 rounded-2xl shadow-lg max-w-sm w-full">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="text-navy" />
            <h1 className="text-2xl font-bold text-navy">Admin · Sync Health</h1>
          </div>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin key"
            className="w-full px-4 py-3 border border-navy/20 rounded-xl mb-3"
            data-testid="sync-health-key-input"
          />
          {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
          <button type="submit" className="w-full bg-navy text-cream py-3 rounded-xl font-semibold" data-testid="sync-health-login-btn">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  const probe = health?.probe || {};
  const inbound = health?.inbound || {};
  const outbound = health?.outbound || {};
  const lastSession = inbound.last_session;
  const lastImp = inbound.last_impression;

  return (
    <div className="min-h-screen bg-cream-light py-8 px-4 sm:px-6 lg:px-8" data-testid="sync-health-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/admin/geofences" className="text-navy/60 hover:text-navy text-sm flex items-center gap-1 mb-1">
              <ArrowLeft size={14} /> Back to admin
            </Link>
            <h1 className="text-3xl font-bold text-navy flex items-center gap-2">
              <Activity size={28} /> Mobile App Sync Health
            </h1>
            <p className="text-navy/60 text-sm mt-1">
              Live status of the connection between this server and the ad-time-admin driver app.
            </p>
          </div>
          <button
            onClick={() => fetchHealth(adminKey)}
            disabled={loading}
            className="px-4 py-2 bg-navy text-cream rounded-lg font-semibold text-sm flex items-center gap-2 hover:bg-navy/90 disabled:opacity-50"
            data-testid="sync-health-refresh"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {/* Connection card */}
        <div className="bg-white rounded-2xl p-5 mb-5 shadow-sm" data-testid="sync-health-connection">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-bold text-navy">Fan-out connection (this server → driver app)</h3>
            <StatusBadge ok={probe.reachable} labelOk="Reachable" labelBad="Unreachable" />
          </div>
          <div className="text-sm text-navy/70 space-y-1.5">
            <p>
              <span className="text-navy/50">URL:</span>{' '}
              <code className="bg-cream px-1.5 py-0.5 rounded text-xs" data-testid="sync-health-url">
                {health?.ad_time_admin_url || 'Not configured'}
              </code>
            </p>
            {probe.reachable && (
              <p>
                <span className="text-navy/50">Response:</span> HTTP {probe.status_code} in {probe.latency_ms} ms
              </p>
            )}
            {probe.error && (
              <p className="text-red-600">
                <span className="text-navy/50">Error:</span> {probe.error}
              </p>
            )}
            <p className="text-navy/40 text-xs">Checked {timeAgo(health?.checked_at)}</p>
          </div>
          {health?.is_preview_url && (
            <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800" data-testid="sync-health-preview-warning">
              <AlertTriangle size={18} className="shrink-0 mt-0.5" />
              <p>
                This is a <strong>preview URL</strong> — preview environments sleep and expire. Before drivers
                use the published app, deploy ad-time-admin to production and update{' '}
                <code className="bg-amber-100 px-1 rounded">AD_TIME_ADMIN_URL</code> in this server's environment.
              </p>
            </div>
          )}
        </div>

        {/* Inbound card */}
        <div className="bg-white rounded-2xl p-5 mb-5 shadow-sm" data-testid="sync-health-inbound">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-bold text-navy flex items-center gap-2">
              <ArrowDownToLine size={18} /> Inbound — driver sessions received
            </h3>
            <StatusBadge
              ok={(inbound.sessions_7d || 0) > 0}
              labelOk="Receiving data"
              labelBad="Nothing in 7 days"
            />
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              ['Last 24 hrs', inbound.sessions_24h],
              ['Last 7 days', inbound.sessions_7d],
              ['All time', inbound.total_sessions],
            ].map(([label, val]) => (
              <div key={label} className="bg-cream rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-navy">{val ?? 0}</p>
                <p className="text-xs text-navy/50">{label}</p>
              </div>
            ))}
          </div>
          {lastSession ? (
            <div className="text-sm text-navy/70 space-y-1" data-testid="sync-health-last-session">
              <p className="font-semibold text-navy">Most recent session</p>
              <p>
                <span className="text-navy/50">Received:</span> {timeAgo(lastSession.imported_at)}{' '}
                <span className="text-navy/40 text-xs">({new Date(lastSession.imported_at).toLocaleString()})</span>
              </p>
              <p>
                <span className="text-navy/50">Driver:</span> {lastSession.driver_name || '—'} ·{' '}
                <span className="text-navy/50">Campaign:</span> {lastSession.company_name || lastSession.advertiser_id} ·{' '}
                <span className="text-navy/50">Session date:</span> {lastSession.date}
              </p>
            </div>
          ) : (
            <p className="text-sm text-red-600" data-testid="sync-health-no-sessions">
              No driver sessions have ever been received from the mobile app.
            </p>
          )}
          {lastImp && (
            <p className="text-sm text-navy/70 mt-2">
              <span className="text-navy/50">Last daily impression push:</span>{' '}
              {lastImp.impressions?.toLocaleString()} impressions for {lastImp.date} · received {timeAgo(lastImp.updated_at)}
            </p>
          )}
          {inbound.last_progress && (
            <p className="text-sm text-navy/70 mt-2" data-testid="sync-health-last-progress">
              <span className="text-navy/50">Last live progress ping:</span>{' '}
              {inbound.last_progress.campaign_name || inbound.last_progress.campaign_code} ·{' '}
              {inbound.last_progress.summary?.active_drivers ?? 0}/{inbound.last_progress.summary?.total_drivers ?? 0} drivers active ·{' '}
              received {timeAgo(inbound.last_progress.received_at)}
            </p>
          )}
        </div>

        {/* Drive health alerts */}
        <div className="bg-white rounded-2xl p-5 mb-5 shadow-sm" data-testid="drive-alerts-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-bold text-navy flex items-center gap-2">
              <AlertTriangle size={18} /> Drive health alerts
            </h3>
            <div className="flex items-center gap-2">
              <StatusBadge
                ok={alerts.length === 0}
                labelOk="No problems"
                labelBad={`${inbound.open_alerts ?? alerts.length} open`}
              />
              {alerts.length > 0 && (
                <button
                  onClick={dismissAll}
                  className="text-xs font-semibold text-navy/60 hover:text-navy underline"
                  data-testid="dismiss-all-alerts-btn"
                >
                  Dismiss all
                </button>
              )}
            </div>
          </div>
          <p className="text-navy/50 text-xs mb-3">
            Every inbound drive is screened for a missing route, an unknown advertiser, an
            impossible or zero duration, or paid hours exceeding wall-clock time.
          </p>
          {alerts.length === 0 ? (
            <p className="text-sm text-navy/60" data-testid="no-drive-alerts">
              Nothing flagged. Every drive received so far had a usable advertiser, a real route
              and a believable duration.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="drive-alerts-list">
              {alerts.map((a) => (
                <li
                  key={a.id}
                  className={`rounded-xl p-3 border text-sm flex items-start justify-between gap-3 ${
                    a.severity === 'critical'
                      ? 'bg-red-50 border-red-200 text-red-800'
                      : 'bg-amber-50 border-amber-200 text-amber-800'
                  }`}
                  data-testid={`drive-alert-${a.kind}`}
                >
                  <div>
                    <p className="font-semibold uppercase text-[11px] tracking-wide">
                      {a.severity} · {a.kind.replace(/_/g, ' ')}
                    </p>
                    <p className="mt-0.5">{a.detail}</p>
                    <p className="text-[11px] opacity-70 mt-1">{timeAgo(a.created_at)}</p>
                  </div>
                  <button
                    onClick={() => dismissAlert(a.id)}
                    className="text-xs font-semibold underline shrink-0"
                    data-testid={`dismiss-alert-${a.id}`}
                  >
                    Dismiss
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Outbound card */}
        <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="sync-health-outbound">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-bold text-navy flex items-center gap-2">
              <ArrowUpFromLine size={18} /> Outbound — campaign pushes to driver app
            </h3>
            {outbound.last_fanout && (
              <StatusBadge ok={outbound.last_fanout.ok} labelOk="Last push OK" labelBad="Last push failed" />
            )}
          </div>
          {(outbound.recent_fanouts || []).length === 0 ? (
            <p className="text-sm text-navy/60">
              No campaign pushes recorded yet. A push fires automatically each time you approve a lead on{' '}
              <Link to="/admin/leads" className="underline">Admin · Leads</Link>. (Pushes made before this panel
              was added were not recorded.)
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-navy/50 text-left text-xs uppercase border-b border-navy/10">
                  <tr>
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 px-3">Campaign</th>
                    <th className="py-2 px-3">Zones</th>
                    <th className="py-2 px-3">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {outbound.recent_fanouts.map((f) => (
                    <tr key={f.id} className="border-b border-navy/5">
                      <td className="py-2 pr-3 whitespace-nowrap text-navy/70">{timeAgo(f.at)}</td>
                      <td className="py-2 px-3 text-navy">{f.company_name || f.email}</td>
                      <td className="py-2 px-3">{f.zones_count}</td>
                      <td className="py-2 px-3">
                        {f.ok ? (
                          <span className="text-green-700 font-semibold">OK ({f.status_code})</span>
                        ) : (
                          <span className="text-red-600 font-semibold" title={f.detail}>
                            Failed{f.status_code ? ` (${f.status_code})` : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSyncHealth;
